import { Box, Button, ListItemIcon, Menu, MenuItem, Stack } from '@mui/material'
import { FC, useCallback } from 'react'
import { MouseEventHandler, useMemo, useState } from 'react'
import { useCopyToClipboard } from 'usehooks-ts'
import { IconButton, Icons, InformationModal } from 'utoniq-ui'

import {
  ConsoleItemDetailQuery,
  ItemType,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ThumbnailFullScreenModal } from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateFullPath } from '@/lib/url'

import { ConsoleItemDetailAudio } from './ConsoleItemDetailAudio'
import { ConsoleItemDetailAvatar } from './ConsoleItemDetailAvatar'
import {
  ConsoleItemDetailMagazine,
  MagazinePlayerModal,
} from './ConsoleItemDetailMagazine'
import { ConsoleItemDetailMerch } from './ConsoleItemDetailMerch'
import { ConsoleItemDetailTicket } from './ConsoleItemDetailTicket'
import { ConsoleItemDetailVideo } from './ConsoleItemDetailVideo'
import { ConsoleItemDetailWallpaper } from './ConsoleItemDetailWallpaper'
import {
  ConsoleSupplementLinkArea,
  ConsoleSupplementLinkAreaProps,
} from './ConsoleSupplementLinkArea'

export * from './fragments'
type TicketFilterType = 'general' | 'used' | 'unused'

export type State = {
  artistId: string
  loading: boolean
  refetchingSerial: boolean
  hasNextSerialPage: boolean
  item: Maybe<ConsoleItemDetailQuery['item']>
  ticketFilter: TicketFilterType
  ticketOpen?: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onToggleActive: ConsoleSupplementLinkAreaProps['onToggleActive']
  onToggleSelling: ConsoleSupplementLinkAreaProps['onToggleSelling']
  onRemove: () => Promise<void>
  onSettingAvatar: () => Promise<void>
  onFetchMoreSerial: () => Promise<void>
  onFilterTicket: (filter: TicketFilterType) => Promise<void>
  onToggleTicketUsed: (
    serialId: string,
    isUsed: boolean,
    throughTicketName?: string
  ) => Promise<void>
}

export const ConsoleItemDetailTemplate: Template<State, Action> = ({
  state,
  onToggleSelling,
  onToggleActive,
  onRemove,
  onSettingAvatar,
  onFetchMoreSerial,
  onFilterTicket,
  onToggleTicketUsed,
}) => {
  const { routerNames, routerPages } = useRouter()
  // マガジンアイテムの表示に関する状態
  const [displayResource, setDisplayResource] = useState(false)

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleItemDetail} />

      <ConsoleBaseLayout
        safeArea={displayResource ? { pt: 0, pb: 0 } : undefined}
        header={
          displayResource
            ? undefined
            : {
                back: true,
                defaultBackPage: routerPages.ConsoleItems,
                type: 'modal',
                right: (
                  <Option artistId={state.artistId} itemId={state.item?.id} />
                ),
                color: 'white',
              }
        }
        sx={{
          height: '100dvh',
          // マガジンビュワーのスタイル設定
          '.BaseLayout': {
            position: 'fixed',
            width: '100%',
            top: 0,
            left: 0,
          },
        }}
      >
        <ItemDetailCell
          artistId={state.artistId}
          item={state.item}
          loading={state.loading}
          ticketOpen={state.ticketOpen}
          refetchingSerial={state.refetchingSerial}
          hasNextSerialPage={state.hasNextSerialPage}
          onToggleActive={onToggleActive}
          onToggleSelling={onToggleSelling}
          onRemove={onRemove}
          onSettingAvatar={onSettingAvatar}
          onFetchMoreSerial={onFetchMoreSerial}
          ticketFilter={state.ticketFilter}
          onFilterTicket={onFilterTicket}
          onToggleTicketUsed={onToggleTicketUsed}
          displayResource={displayResource}
          setDisplayResource={setDisplayResource}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type ItemDetailCellProps = {
  artistId: State['artistId']
  item: State['item']
  loading: State['loading']
  ticketOpen: State['ticketOpen']
  refetchingSerial: State['refetchingSerial']
  hasNextSerialPage: State['hasNextSerialPage']
  ticketFilter: State['ticketFilter']
  onToggleActive: Action['onToggleActive']
  onToggleSelling: Action['onToggleSelling']
  onRemove: Action['onRemove']
  onSettingAvatar: Action['onSettingAvatar']
  onFetchMoreSerial: Action['onFetchMoreSerial']
  onFilterTicket: Action['onFilterTicket']
  onToggleTicketUsed: Action['onToggleTicketUsed']
  displayResource: boolean
  setDisplayResource: (value: boolean) => void
}
const ItemDetailCell: Cell<ItemDetailCellProps> = ({
  artistId,
  item,
  loading,
  ticketOpen,
  refetchingSerial,
  ticketFilter,
  hasNextSerialPage,
  onToggleActive,
  onToggleSelling,
  onRemove,
  onSettingAvatar,
  onFetchMoreSerial,
  onFilterTicket,
  onToggleTicketUsed,
  displayResource,
  setDisplayResource,
}) => {
  const { t } = useTranslation('consoleItemDetail')
  const { showMessage, showError } = useMessage()
  const [openFullScreenModal, setOpenFullScreenModal] = useState(false)
  const [showMultipleImage, setShowMultipleImage] = useState(false)

  const [openTrashConfirm, setOpenTrashConfirm] = useState(false)
  const [removing, setRemoving] = useState(false)
  // カードの表裏に関する状態
  const [isFlipped, setIsFlipped] = useState(false)
  const handleReverseThumbnail = useCallback(
    () => setIsFlipped(prev => !prev),
    [setIsFlipped]
  )

  const itemDetailComponent = useMemo(() => {
    switch (item?.type) {
      case ItemType.Avatar:
        return (
          <ConsoleItemDetailAvatar
            loading={loading}
            item={item}
            updating={false}
            onSettingAvatar={onSettingAvatar}
            isFlipped={isFlipped}
            onReverseThumbnail={handleReverseThumbnail}
            onOpenFullScreen={() => setOpenFullScreenModal(true)}
            onOpenMultipleImage={() => {
              setOpenFullScreenModal(true)
              setShowMultipleImage(true)
            }}
          />
        )
      case ItemType.Magazine:
        return (
          <ConsoleItemDetailMagazine
            loading={loading}
            item={item}
            isFlipped={isFlipped}
            onReverseThumbnail={handleReverseThumbnail}
            onViewMagazine={() => setDisplayResource(true)}
            onOpenFullScreen={() => setOpenFullScreenModal(true)}
            onOpenMultipleImage={() => {
              setOpenFullScreenModal(true)
              setShowMultipleImage(true)
            }}
          />
        )
      case ItemType.Merch:
        return (
          <ConsoleItemDetailMerch
            loading={loading}
            item={item}
            isFlipped={isFlipped}
            onReverseThumbnail={handleReverseThumbnail}
            onOpenFullScreen={() => setOpenFullScreenModal(true)}
            onOpenMultipleImage={() => {
              setOpenFullScreenModal(true)
              setShowMultipleImage(true)
            }}
          />
        )
      case ItemType.Video:
        return (
          <ConsoleItemDetailVideo
            loading={loading}
            item={item}
            isFlipped={isFlipped}
            onReverseThumbnail={handleReverseThumbnail}
            onOpenFullScreen={() => setOpenFullScreenModal(true)}
            onOpenMultipleImage={() => {
              setOpenFullScreenModal(true)
              setShowMultipleImage(true)
            }}
          />
        )
      case ItemType.Wallpaper:
        return (
          <ConsoleItemDetailWallpaper
            loading={loading}
            item={item}
            isFlipped={isFlipped}
            onReverseThumbnail={handleReverseThumbnail}
            onOpenFullScreen={() => setOpenFullScreenModal(true)}
            onOpenMultipleImage={() => {
              setOpenFullScreenModal(true)
              setShowMultipleImage(true)
            }}
          />
        )
      case ItemType.Ticket:
        return (
          <ConsoleItemDetailTicket
            refetching={refetchingSerial}
            hasNextPage={hasNextSerialPage}
            loading={loading}
            item={item}
            ticketOpen={ticketOpen}
            artistId={artistId}
            onToggleActive={onToggleActive}
            onToggleSelling={onToggleSelling}
            onRemove={() => setOpenTrashConfirm(true)}
            onFetchMore={onFetchMoreSerial}
            isFlipped={isFlipped}
            onReverseThumbnail={handleReverseThumbnail}
            ticketFilter={ticketFilter}
            onFilterTicket={onFilterTicket}
            onToggleTicketUsed={onToggleTicketUsed}
            onOpenFullScreen={() => setOpenFullScreenModal(true)}
            onOpenMultipleImage={() => {
              setOpenFullScreenModal(true)
              setShowMultipleImage(true)
            }}
          />
        )
      default:
        return (
          <ConsoleItemDetailAudio
            loading={loading}
            item={item}
            isFlipped={isFlipped}
            onReverseThumbnail={handleReverseThumbnail}
            onOpenFullScreen={() => setOpenFullScreenModal(true)}
            onOpenMultipleImage={() => {
              setOpenFullScreenModal(true)
              setShowMultipleImage(true)
            }}
          />
        )
    }
  }, [
    item,
    loading,
    onSettingAvatar,
    isFlipped,
    handleReverseThumbnail,
    refetchingSerial,
    hasNextSerialPage,
    artistId,
    onToggleActive,
    onToggleSelling,
    onFetchMoreSerial,
    ticketFilter,
    onFilterTicket,
    onToggleTicketUsed,
    setDisplayResource,
  ])

  const handleRemove = useCallback(async () => {
    setRemoving(true)
    await onRemove()
      .then(() => {
        showMessage(t('deletedItem'), 'success')
      })
      .catch(error => showError(error))
      .finally(() => {
        setOpenTrashConfirm(false)
        setRemoving(false)
      })
  }, [onRemove, showMessage, t, showError])

  return (
    <CellFrame
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
      }}
    >
      <Stack
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        {itemDetailComponent}
      </Stack>
      <ConsoleSupplementLinkArea
        loading={loading}
        itemId={item?.id ?? '#'}
        isPublished={!!item?.isPublished}
        saleStatus={item?.salesStatus ?? ''}
        onToggleActive={onToggleActive}
        onToggleSelling={onToggleSelling}
        onRemove={() => setOpenTrashConfirm(true)}
        sx={{ visibility: !displayResource ? 'visible' : 'hidden' }}
      />
      <MagazinePlayerModal
        loading={loading}
        open={displayResource}
        item={item}
        onClose={() => setDisplayResource(false)}
      />
      <ThumbnailFullScreenModal
        isDrm={item?.isDrm}
        open={openFullScreenModal}
        onClose={() => {
          setOpenFullScreenModal(false)
          setShowMultipleImage(false)
        }}
        thumbnail={item?.thumbnail}
        images={item?.images}
        showMultipleImage={showMultipleImage}
        isFlipped={isFlipped}
      />
      <InformationModal
        open={openTrashConfirm}
        onClose={() => setOpenTrashConfirm(false)}
        title={t('deleteItemModalTitle')}
        content={t('deleteItemModalDesc')}
        cancelText={t('cancel')}
        buttons={
          <Button
            size="large"
            color="primary"
            loading={removing}
            sx={{ minWidth: 212 }}
            onClick={handleRemove}
          >
            {t('deleteBtn')}
          </Button>
        }
      />
    </CellFrame>
  )
}

/**
 * アイテムのオプションメニュー表示
 */
const Option: FC<{ itemId: string | undefined; artistId: string }> = ({
  itemId,
  artistId,
}) => {
  const { t } = useTranslation('consoleItemDetail')
  const { routerPages } = useRouter()
  const { showMessage } = useMessage()
  const [_, copy] = useCopyToClipboard()
  const { onClick } = useBrowserLink(
    generateFullPath(routerPages.ArtistStoreItemDetail, {
      artistId: artistId ?? '#',
      itemId: itemId ?? '#',
    })
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const onToggleOption: MouseEventHandler<HTMLElement> = e => {
    setAnchorEl(e.currentTarget)
  }

  const open = !!anchorEl

  if (!itemId || !artistId) return null

  const options = [
    {
      label: t('copyURLLabel'),
      disabled: !itemId || !artistId,
      async onClick() {
        await copy(
          generateFullPath(routerPages.ArtistStoreItemDetail, {
            artistId: artistId,
            itemId: itemId,
          })
        )
        showMessage(t('copyURLSuccess'), 'success')
        setAnchorEl(null)
      },
    },
    {
      label: t('openStoreLabel'),
      icon: (
        <ListItemIcon sx={{ '&.MuiListItemIcon-root': { minWidth: 24 } }}>
          <Icons.Window fontSize="xSmall" />
        </ListItemIcon>
      ),
      onClick() {
        onClick()
        setAnchorEl(null)
      },
    },
  ]

  return (
    <Box>
      <IconButton.More
        bgcolor="transparent"
        color="white"
        size="xSmall"
        onClick={onToggleOption}
      />
      <Menu
        id="item-option"
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {options.map(({ icon, label, ...option }) => (
          <MenuItem {...option} sx={{ width: 200 }} key={label}>
            {icon}
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
